import { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import Navbar from "../../components/navbar/Navbar";
import styled from "./StudentDetails.module.css";
import { SubmitButton } from "../../components/styled/styledbuttons/buttons";
import { db, auth } from "../../firebase/firebase";
import {
  doc,
  getDoc,
  collection,
  addDoc,
  getDocs,
  query,
  where,
  serverTimestamp,
} from "firebase/firestore";
import maleAvatar from "../../assets/maleAvatar.webp";

function formatWhen(value) {
  if (!value) return "";
  const date = value?.toDate?.() || new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("en-GB");
}

const StudentDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const passedStudent = location.state?.student;
  const [student, setStudent] = useState(null);
  const [comment, setComment] = useState("");
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const loadComments = async (studentId) => {
    const uid = auth.currentUser?.uid;
    if (!studentId || !uid) {
      setComments([]);
      return;
    }

    const commentsRef = collection(db, "students", studentId, "comments");
    const [approvedSnap, mineSnap] = await Promise.all([
      getDocs(query(commentsRef, where("status", "==", "approved"))),
      getDocs(
        query(
          commentsRef,
          where("teacherId", "==", uid),
          where("status", "==", "pending")
        )
      ),
    ]);

    const rows = [
      ...approvedSnap.docs.map((d) => ({ id: d.id, ...d.data() })),
      ...mineSnap.docs.map((d) => ({ id: d.id, ...d.data() })),
    ];

    rows.sort((a, b) => {
      const aTime = a.createdAt?.toMillis?.() || 0;
      const bTime = b.createdAt?.toMillis?.() || 0;
      return bTime - aTime;
    });
    setComments(rows);
  };

  useEffect(() => {
    const fetchStudent = async () => {
      setLoading(true);
      setError("");
      try {
        if (passedStudent) {
          setStudent(passedStudent);
          await loadComments(passedStudent.id || id);
          return;
        }

        const studentRef = doc(db, "students", id);
        const snap = await getDoc(studentRef);
        if (snap.exists()) {
          setStudent({ id: snap.id, ...snap.data() });
          await loadComments(snap.id);
        } else {
          setStudent(null);
        }
      } catch (err) {
        setError(err.message || "Failed to load student");
        setStudent(null);
      } finally {
        setLoading(false);
      }
    };

    fetchStudent();
  }, [id, passedStudent]);

  const handleCommentSubmit = async () => {
    if (!comment.trim()) {
      alert("Comment cannot be empty");
      return;
    }
    if (!auth.currentUser) {
      alert("Not signed in");
      return;
    }

    setSubmitting(true);
    setError("");
    try {
      await addDoc(collection(db, "students", id, "comments"), {
        text: comment.trim(),
        teacherId: auth.currentUser.uid,
        teacherName: auth.currentUser.displayName || "Teacher",
        createdAt: serverTimestamp(),
        status: "pending",
      });
      setComment("");
      alert("Comment submitted for admin approval");
      await loadComments(id);
    } catch (err) {
      setError(err.message || "Failed to submit comment");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <p>Loading student...</p>;
  if (!student) return <p>{error || "Student not found"}</p>;

  return (
    <>
      <Navbar />

      <div className={styled.container}>
        <div className={styled.infoSide}>
          <img
            className={styled.img}
            src={student.profileImage || maleAvatar}
            alt="avatar"
          />
          <h3>
            {student.firstName} {student.lastName}
          </h3>
          <h3>{student.gender}</h3>

          <SubmitButton
            className={styled.goBackBtn}
            onClick={() => navigate(-1)}
          >
            ← Go Back
          </SubmitButton>
        </div>

        <div className={styled.commentSide}>
          <h3>Comments</h3>
          <p style={{ fontSize: "0.9rem", opacity: 0.8 }}>
            New comments stay hidden until an admin approves them. You can see
            your own pending notes here.
          </p>

          {error && <p style={{ color: "#8b2e2e" }}>{error}</p>}

          <ul style={{ listStyle: "none", padding: 0 }}>
            {comments.length === 0 && <li>No comments yet.</li>}
            {comments.map((c) => (
              <li
                key={c.id}
                style={{
                  borderTop: "1px solid #ddd",
                  padding: "0.75rem 0",
                }}
              >
                <div>{c.text}</div>
                <div style={{ fontSize: "0.85rem", opacity: 0.75 }}>
                  {c.teacherName || "Teacher"} · {formatWhen(c.createdAt)}
                  {c.status === "pending" ? " · Pending approval" : ""}
                </div>
              </li>
            ))}
          </ul>

          <div className={styled.inputDiv}>
            <label>Leave a comment</label>

            <textarea
              className={styled.comment}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              maxLength={1500}
            />

            <SubmitButton onClick={handleCommentSubmit} disabled={submitting}>
              {submitting ? "Submitting…" : "Submit for approval"}
            </SubmitButton>
          </div>
        </div>
      </div>
    </>
  );
};

export default StudentDetails;
